import { Box, Button, List, ListItem, Skeleton, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as ReactRouterLink, useParams } from 'react-router-dom';

import {
  ActionBar,
  Alert,
  EntityPermission,
  NotableMarkdown,
  StandardPageBody,
  StandardPageLayout,
  Text,
  api,
  getQueryProps,
  isDefined,
  isTruthy,
  useLoggedInAuthContext,
} from 'common';

import { FlowTemplatePreviewCaseStudy } from './CaseStudy';
import { FlowTemplatePreviewDemoVideo } from './DemoVideo';
import { FlowTemplatePreviewPanel } from './Panel';
import { FlowTemplatePreviewUseTemplateDialog } from './UseTemplateDialog';
import { EventAction, EventCategory, useCaptureEvents } from '../../../../../utils/events/index';
import { FlowCreationAction } from '../../../Flows/types';
import { useAgentApi } from '../../../common/useAgentApi';
import { FlowTemplateDefinitionPreview } from '../../Flow/FlowTemplateDefinitionPreview';

const MARKDOWN_CONTENT_MAX_WIDTH = 850;

export function FlowTemplatePreviewPage() {
  const { hasPermission, hasViewAgentOrFlowBuilderAncillaryPermissions } = useLoggedInAuthContext();
  const { templateId, agentId } = useParams<{
    templateId: string;
    /**
     * This menu is on two routes, one with agentId and one without
     */
    agentId?: string;
  }>();

  const [imageTimestamp] = useState<string>(new Date().getTime().toString());
  const [isNewFlowDialogOpen, setIsNewFlowDialogOpen] = useState(false);
  const { captureEvent } = useCaptureEvents();
  const {
    data: flowTemplate,
    error,
    isPending: isPendingFlowTemplate,
  } = useQuery({
    ...getQueryProps(api.client.FlowDefinitionTemplate, 'findOneById', templateId),
    enabled: templateId.length > 10,
  });

  const { agentPermissions, agentResponse } = useAgentApi({ agentId });

  const isPending = isPendingFlowTemplate || (isDefined(agentId) && agentResponse.isPending);

  return (
    <>
      <StandardPageLayout
        actionBar={
          <ActionBar
            breadcrumbProps={{
              activeRoute: { label: flowTemplate?.name ?? 'Template', isPending },
              links: [
                ...(hasViewAgentOrFlowBuilderAncillaryPermissions
                  ? [{ label: 'Agents', to: '/agents' }]
                  : []),
                ...(isDefined(agentId)
                  ? [
                      {
                        label: agentResponse.data?.name ?? 'Agent Details',
                        to: `/agents/agents/${agentId}`,
                        isPending: agentResponse.isPending,
                      },
                    ]
                  : []),
                {
                  label: 'Flow Library',
                  to: `/agents${isDefined(agentId) ? `/agents/${agentId}` : ''}/flows/create`,
                },
              ],
            }}
            actions={
              !isPending &&
              !error &&
              isDefined(flowTemplate?.flowDefinitionId) && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {agentPermissions[EntityPermission.Modify] &&
                    (hasPermission.ModifyFlowDefinitionTemplates ||
                      hasPermission.PublishFlowDefinitionTemplates) && (
                      <Button
                        variant="outlined"
                        component={ReactRouterLink}
                        to={`./${flowTemplate.id}/edit`}
                        onClick={() => {
                          captureEvent({
                            category: EventCategory.FlowDefinitionTemplate,
                            action: EventAction.EditButtonClick,
                            properties: {
                              templateId: flowTemplate.id,
                              templateName: flowTemplate.name,
                            },
                          });
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  {isTruthy(agentPermissions[EntityPermission.Modify]) && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setIsNewFlowDialogOpen(true);
                        captureEvent({
                          category: EventCategory.FlowDefinitionTemplate,
                          action: EventAction.UseTemplateClick,
                          properties: {
                            templateId: flowTemplate.id,
                            templateName: flowTemplate.name,
                          },
                        });
                      }}
                    >
                      Use Template
                    </Button>
                  )}
                </Stack>
              )
            }
          />
        }
      >
        <StandardPageBody>
          {isPending && <Skeleton variant="rectangular" width="100%" height={280} />}

          {isDefined(error) && <Alert error={error}>Unable to find template</Alert>}

          {!isPending && !isDefined(error) && isDefined(flowTemplate) && (
            <>
              <Stack width="100%" gap={3}>
                <FlowTemplateDefinitionPreview
                  templateId={templateId}
                  height={280}
                  borderRadiusInPixels={8}
                />
                <Stack direction="row" gap={5} sx={{ flex: 1, justifyContent: 'center' }}>
                  <FlowTemplatePreviewPanel flowTemplate={flowTemplate} />
                  <Stack
                    gap={3}
                    sx={{ flex: 1, minWidth: 0, maxWidth: MARKDOWN_CONTENT_MAX_WIDTH }}
                  >
                    <Stack gap={3} sx={{ overflowY: 'auto', fontSize: '1rem' }}>
                      {isTruthy(flowTemplate.metadata?.overview) && (
                        <NotableMarkdown>{flowTemplate.metadata.overview}</NotableMarkdown>
                      )}
                      <FlowTemplatePreviewCaseStudy
                        caseStudy={flowTemplate.metadata?.caseStudy}
                        imageTimestamp={imageTimestamp}
                      />
                      {isDefined(flowTemplate.metadata?.demoVideo) && (
                        <FlowTemplatePreviewDemoVideo demoVideo={flowTemplate.metadata.demoVideo} />
                      )}
                      {isTruthy(flowTemplate.detailedContentMarkdown) && (
                        <NotableMarkdown>{flowTemplate.detailedContentMarkdown}</NotableMarkdown>
                      )}
                      {!isTruthy(flowTemplate.detailedContentMarkdown) &&
                        !isTruthy(flowTemplate.metadata?.overview) && (
                          <Stack gap={3}>
                            <Text variant="heading3" bold>
                              Give your flow some context
                            </Text>
                            <Box>
                              <Text variant="body1">
                                Help your team hit the ground running. Provide the essential context
                                for this flow, including:
                              </Text>
                              <List
                                sx={{
                                  paddingLeft: 3,
                                }}
                              >
                                <ListItem
                                  disablePadding
                                  sx={{ display: 'list-item', listStyleType: 'disc' }}
                                >
                                  <Text variant="body1">
                                    <b>Goals:</b> The core purpose of the flow.
                                  </Text>
                                </ListItem>
                                <ListItem
                                  disablePadding
                                  sx={{ display: 'list-item', listStyleType: 'disc' }}
                                >
                                  <Text variant="body1">
                                    <b>Infrastructure:</b> Required integrations, connectors, and
                                    resources.
                                  </Text>
                                </ListItem>
                                <ListItem
                                  disablePadding
                                  sx={{ display: 'list-item', listStyleType: 'disc' }}
                                >
                                  <Text variant="body1">
                                    <b>Deployment:</b> Estimated time and compatible EHR systems.
                                  </Text>
                                </ListItem>
                                <ListItem
                                  disablePadding
                                  sx={{ display: 'list-item', listStyleType: 'disc' }}
                                >
                                  <Text variant="body1">
                                    <b>Impact:</b> The specific value this workflow provides.
                                  </Text>
                                </ListItem>
                              </List>
                            </Box>
                            {agentPermissions[EntityPermission.Modify] &&
                              (hasPermission.ModifyFlowDefinitionTemplates ||
                                hasPermission.PublishFlowDefinitionTemplates) && (
                                <Box>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    component={ReactRouterLink}
                                    to={`./${flowTemplate.id}/edit`}
                                    onClick={() => {
                                      captureEvent({
                                        category: EventCategory.FlowDefinitionTemplate,
                                        action: EventAction.EditButtonClick,
                                        properties: {
                                          templateId: flowTemplate.id,
                                          templateName: flowTemplate.name,
                                        },
                                      });
                                    }}
                                  >
                                    Add Details
                                  </Button>
                                </Box>
                              )}
                          </Stack>
                        )}
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </>
          )}
        </StandardPageBody>
      </StandardPageLayout>
      {isDefined(flowTemplate?.flowDefinitionId) && (
        <FlowTemplatePreviewUseTemplateDialog
          isOpen={isNewFlowDialogOpen}
          onClose={() => setIsNewFlowDialogOpen(false)}
          flowTemplateId={flowTemplate.id}
          createFlowValues={{
            name: flowTemplate.name,
            sourceFlowDefinitionId: flowTemplate.flowDefinitionId,
          }}
          agentId={agentId}
          organizationId={agentResponse?.data?.organizationId}
          flowCreationAction={FlowCreationAction.CreateFromFlowTemplate}
        />
      )}
    </>
  );
}
